import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

// Client-side Google Website Translator. Translates the whole app (UI + content)
// via the googtrans cookie + Google's hidden widget. No backend, fully reversible.
const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "te", label: "తెలుగు" },
  { code: "ta", label: "தமிழ்" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "bn", label: "বাংলা" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

const getCurrentLang = () => {
  const m = document.cookie.match(/googtrans=\/en\/(\w+)/);
  return m ? m[1] : "en";
};

const setGoogTransCookie = (code) => {
  const host = window.location.hostname;
  const expire = "expires=" + new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString();
  const clear = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const domains = ["", `domain=${host};`, `domain=.${host};`];
  if (code === "en") {
    domains.forEach((d) => { document.cookie = `googtrans=;${d}path=/;${clear}`; });
  } else {
    const val = `/en/${code}`;
    domains.forEach((d) => { document.cookie = `googtrans=${val};${d}path=/;${expire}`; });
  }
};

let scriptInjected = false;
const injectGoogleScript = () => {
  if (scriptInjected || (window.google && window.google.translate)) return;
  scriptInjected = true;
  window.googleTranslateElementInit = () => {
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_translate_element"
    );
  };
  const s = document.createElement("script");
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
};

export const LanguageSwitcher = () => {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    injectGoogleScript();
    setLang(getCurrentLang());
  }, []);

  const onChange = (e) => {
    const code = e.target.value;
    setGoogTransCookie(code);
    window.location.reload();
  };

  const currentLabel = (LANGS.find((l) => l.code === lang) || LANGS[0]).label;

  return (
    <div className="notranslate relative flex items-center flex-shrink-0" title="Translate site">
      {/* Visible trigger — full pill (globe + language + chevron) on all sizes */}
      <div className="flex items-center gap-1 pl-2 pr-2 py-1.5 rounded-full bg-slate-100 text-slate-700 text-[10px] sm:text-xs font-medium pointer-events-none">
        <Globe className="w-3.5 h-3.5 text-slate-500" />
        <span>{currentLabel}</span>
        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {/* Transparent native select overlay — captures taps, shows readable options */}
      <select
        value={lang}
        onChange={onChange}
        data-testid="language-switcher"
        aria-label="Select language"
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-slate-700"
      >
        {LANGS.map((l) => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
      <div id="google_translate_element" className="hidden" />
    </div>
  );
};

export default LanguageSwitcher;
